import type {
  CreateCompetitionIframeOptions,
  CreateIframeOptions,
  CreateUpcomingEventsIframeOptions,
  CreateWidgetIframeOptions,
  VexiaWidgetType,
} from "./types";

export type {
  CreateCompetitionIframeOptions,
  CreateIframeOptions,
  CreateUpcomingEventsIframeOptions,
  CreateWidgetIframeOptions,
  VexiaWidgetType,
} from "./types";

// const APP_BASE_URL = "http://localhost:3001";
const APP_BASE_URL = "https://vexia-widgets--vexia-widgets.europe-west4.hosted.app";
const DEFAULT_DYNAMIC_HEIGHT_EVENT_NAME = "vexiaWidgetsIframeResizer";
const DEFAULT_SECTION_SCROLL_EVENT_NAME = "vexiaWidgetsScrollTo";

function getCompetitionUrl(competitionId: string): string {
  return `${APP_BASE_URL}/competitions/${encodeURIComponent(competitionId)}`;
}

function getUpcomingEventsUrl(competitionId: string): string {
  return `${APP_BASE_URL}/widgets/upcoming-events/${encodeURIComponent(
    competitionId,
  )}`;
}

function getWidgetUrl(
  type: VexiaWidgetType,
  competitionId: string,
  params?: Record<string, string>,
): string {
  const base = `${APP_BASE_URL}/widgets/${encodeURIComponent(
    type,
  )}/${encodeURIComponent(competitionId)}`;

  if (!params) {
    return base;
  }

  const query = new URLSearchParams(params).toString();
  return query ? `${base}?${query}` : base;
}

function getResizeMessageHeight(
  message: MessageEvent<unknown>,
  messageType: string,
): number | undefined {
  if (
    typeof message.data !== "object" ||
    message.data === null ||
    !("type" in message.data) ||
    message.data.type !== messageType ||
    !("detail" in message.data) ||
    typeof message.data.detail !== "object" ||
    message.data.detail === null ||
    !("height" in message.data.detail)
  ) {
    return undefined;
  }

  const rawHeight = message.data.detail.height;

  if (typeof rawHeight === "number" && Number.isFinite(rawHeight)) {
    return rawHeight;
  }

  if (typeof rawHeight === "string") {
    const height = Number.parseFloat(rawHeight);

    if (Number.isFinite(height)) {
      return height;
    }
  }

  return undefined;
}

function enableDynamicHeight(
  iframe: HTMLIFrameElement,
  messageType: string,
): void {
  const handleResizeMessage = (message: MessageEvent<unknown>): void => {
    if (!iframe.isConnected) {
      window.removeEventListener("message", handleResizeMessage);
      return;
    }

    if (message.source !== iframe.contentWindow) {
      return;
    }

    const height = getResizeMessageHeight(message, messageType);

    if (height === undefined) {
      return;
    }

    iframe.style.height = `${height}px`;
  };

  window.addEventListener("message", handleResizeMessage);
}

function getSectionScrollTop(
  message: MessageEvent<unknown>,
  messageType: string,
): number | undefined {
  if (
    typeof message.data !== "object" ||
    message.data === null ||
    !("type" in message.data) ||
    message.data.type !== messageType ||
    !("detail" in message.data) ||
    typeof message.data.detail !== "object" ||
    message.data.detail === null ||
    !("top" in message.data.detail)
  ) {
    return undefined;
  }

  const rawTop = message.data.detail.top;

  if (typeof rawTop === "number" && Number.isFinite(rawTop)) {
    return rawTop;
  }

  return undefined;
}

function enableSectionScroll(
  iframe: HTMLIFrameElement,
  messageType: string,
): void {
  const handleScrollMessage = (message: MessageEvent<unknown>): void => {
    if (!iframe.isConnected) {
      window.removeEventListener("message", handleScrollMessage);
      return;
    }

    if (message.source !== iframe.contentWindow) {
      return;
    }

    const top = getSectionScrollTop(message, messageType);

    if (top === undefined) {
      return;
    }

    const iframeTop = iframe.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: iframeTop + top, behavior: "smooth" });
  };

  window.addEventListener("message", handleScrollMessage);
}

function applyFullWidthStyles(iframe: HTMLIFrameElement): void {
  iframe.style.display = "block";
  iframe.style.width = "100vw";
  iframe.style.maxWidth = "100vw";
  iframe.style.marginLeft = "calc(50% - 50vw)";
}

function createIframe(
  target: HTMLElement,
  src: string,
  defaultTitle: string,
  options: CreateIframeOptions,
): HTMLIFrameElement {
  if (!(target instanceof HTMLElement)) {
    throw new TypeError("target must be an HTMLElement");
  }

  const iframe = document.createElement("iframe");
  iframe.src = src;
  iframe.title = options.title ?? defaultTitle;
  iframe.width = options.width ?? "100%";
  iframe.height = options.height ?? "100%";
  iframe.style.border = "0";
  iframe.style.backgroundColor = options.backgroundColor ?? "white";

  if (options.fullWidth) {
    applyFullWidthStyles(iframe);
  }

  if (options.className) {
    iframe.className = options.className;
  }

  if (options.allow) {
    iframe.allow = options.allow;
  }

  if (options.allowFullscreen) {
    iframe.allowFullscreen = true;
  }

  if (options.dynamicHeight === undefined || options.dynamicHeight) {
    enableDynamicHeight(
      iframe,
      options.dynamicHeightEventName ?? DEFAULT_DYNAMIC_HEIGHT_EVENT_NAME,
    );
  }

  target.appendChild(iframe);

  return iframe;
}

function normalizeCompetitionId(competitionId: string): string {
  if (typeof competitionId !== "string") {
    throw new TypeError("competitionId must be a string");
  }

  const normalized = competitionId.trim();

  if (!normalized) {
    throw new TypeError("competitionId must be a non-empty string");
  }

  return normalized;
}

/**
 * Creates a Vexia competition iframe (full competition page) and appends it to
 * the given DOM element.
 */
export function createCompetitionIframe(
  target: HTMLElement,
  competitionId: string,
  options: CreateCompetitionIframeOptions = {},
): HTMLIFrameElement {
  const id = normalizeCompetitionId(competitionId);
  const hash = typeof window !== "undefined" ? window.location.hash : "";

  const iframe = createIframe(
    target,
    `${getCompetitionUrl(id)}${hash}`,
    "Vexia competition widget",
    options,
  );

  if (options.sectionScroll === undefined || options.sectionScroll) {
    enableSectionScroll(
      iframe,
      options.sectionScrollEventName ?? DEFAULT_SECTION_SCROLL_EVENT_NAME,
    );
  }

  return iframe;
}

/**
 * Creates a Vexia upcoming events iframe (single widget) and appends it to the
 * given DOM element.
 */
export function createUpcomingEventsIframe(
  target: HTMLElement,
  competitionId: string,
  options: CreateUpcomingEventsIframeOptions = {},
): HTMLIFrameElement {
  const id = normalizeCompetitionId(competitionId);
  return createIframe(
    target,
    getUpcomingEventsUrl(id),
    "Vexia upcoming events widget",
    options,
  );
}

/**
 * Creates an iframe for any widget served by the parametrized
 * `/widgets/[type]/[id]` route and appends it to the given DOM element.
 *
 * @example
 * createWidgetIframe(el, "tips", "competition-123", { fullWidth: true });
 */
export function createWidgetIframe(
  target: HTMLElement,
  type: VexiaWidgetType,
  competitionId: string,
  options: CreateWidgetIframeOptions = {},
): HTMLIFrameElement {
  const id = normalizeCompetitionId(competitionId);
  const { params, ...iframeOptions } = options;
  return createIframe(
    target,
    getWidgetUrl(type, id, params),
    `Vexia ${type} widget`,
    iframeOptions,
  );
}

export const addCompetitionWidget = createCompetitionIframe;
export const addUpcomingEventsWidget = createUpcomingEventsIframe;
export const addWidget = createWidgetIframe;
